import React, {useContext, useState} from 'react';
import './maturityTimeline.scss';
import './maturityInstructionForm.scss';
import {formatProductTerm, formatCurrency, formatDate, formatTime} from "../../utils/helpers";
import Button from "../Form/Button/Button";
import ConfirmInstructionButton from "./ConfirmInstructionButton";
import '../SharedComponents/text-styles3.scss'
import downloadIcon from "../../assets/images/download-round.svg";
import envConfig from "../../config/env-config";
import {customerDataContext} from '../../contexts/CustomerDataProvider';
import '../SharedComponents/text-styles3.scss'
import {constructDateTime} from "./MaturityFlowHelperFunctions";
import infoIcon from '../../assets/images/info-icon-outline.svg';
import Checkbox from '../Form/Checkbox/Checkbox';
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";

/** @import {RollOverSummaryProps} from './RollOverSummaryTypes' */
/** @import {Account, Product} from '../../contexts/CustomerDataProviderTypes' */

const isUrlAbsolute = (/** @type {string} */ url) => {
    let r = new RegExp('^(?:[a-z]+:)?//', 'i');
    return (r.test(url));
}

function RollOverSummary(/** @type {RollOverSummaryProps} */ props) {
    const [termsConfirmed, setTermsConfirmed] = useState(false)
    const account = props.account;
    const data = useContext(customerDataContext);

    const getRolloverRateForProduct = (/** @type {Product | undefined} */ product, /** @type {Account} */ account) => {
        let rolloverRate = 0;

        if (account.maturity && account.maturity.availableRolloverRates && product) {
            const rolloverProduct = account.maturity.availableRolloverRates.find(r => r.productId === product.id);
            if (rolloverProduct) {
                rolloverRate = rolloverProduct.rate;
            }
        }

        return rolloverRate;
    }

    const product = data?.data?.customer.products.find(product => product.id === props.instruction?.options?.rolloverAccountProductId);
    const availableRolloverRate = getRolloverRateForProduct(product, account);

    const displayNewToBankRate = product ? product.rate > availableRolloverRate : false;
    const rate = displayNewToBankRate ? product?.rate : availableRolloverRate.toFixed(2);
    const term = formatProductTerm(product?.productTerm);
    const analytics = useUnifiedAnalytics();

    const openDocument = (/** @type {string} */ file) => (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ e) => {
        e.preventDefault();
        const url = isUrlAbsolute(file) ? file : `${envConfig.links.documentUrl}${file}`;

        analytics.trackAnalyticsDownloadEvent(
            'download file',
            { url: url, region: 'important info', type: 'link',action: 'download', 
                productInfo: {
                    productName: '',
                    id: ''
                }}, 
            { fileName: file, fileDescription: file },
            'RollOverSummary|download_click|' + file
        );


        window.open(url, "_blank");
    };

    const rollOverAnalytics = (/** @type {string} */ product, /** @type {string | undefined} */ rolloverProduct, /** @type {string | undefined} */ instruction) => {
        analytics.trackAnalyticsClickEvent(
            "give my instruction",
            window.location.pathname,
            "component left",
            product,
            "maturity",
            {
                "maturityOption": instruction === 'RolloverAllFunds' ? "renew everything" : "renew original",
                "termOption": rolloverProduct
            },
            "link click",
            `instruction|click|${instruction === 'RolloverAllFunds' ? "renew everything" : "renew original"}`,
            "Maturity"
        );
    }

    const onClickBack = () => {
        analytics.trackAnalyticsClickEvent(
            "back",
            `/maturity-instruction/${account.id}`,
            "component right",
            account.product.code,
            "maturity",
            {},
            "link click",
            "navigation|back|maturity",
            "Maturity"
        );

        props.onClickBack();
    }

    const getCurrentBalanceWithCalculatedInterest = (/** @type {Account} */ maturingAccount) => {
        if (!maturingAccount.maturity?.calculatedTotalInterest) {
            return maturingAccount.maturity?.calculatedBalanceAtMaturity;
        }

        const currentBalance = maturingAccount.balance;
        const calculatedMaturity = maturingAccount.maturity.calculatedTotalInterest;

        return currentBalance + calculatedMaturity;
    }

    const getCalculatedInterest = (/** @type {Account} */ maturingAccount) => {
        if (!maturingAccount.maturity?.calculatedTotalInterest && maturingAccount.maturity?.calculatedBalanceAtMaturity) {
            return maturingAccount.maturity?.calculatedBalanceAtMaturity - maturingAccount.balance;
        }

        return maturingAccount.maturity?.calculatedTotalInterest;
    }

    const renderSummaryBox = () => {
        return <div className={"maturity3__terms-box"}>
            <h2>Please read and save these documents</h2>
            <ul style={{margin: "24px 0 8px 0"}}>
                <li>
                    <a href='#' target="_blank" onClick={openDocument(window.raeEnv?.generalTermsFileName || '')}>
                        <img className='maturity3__download-icon' src={downloadIcon} alt={'download icon'}/>
                        <p>General terms</p>
                    </a>
                </li>
                <li>
                    <a href='#' target="_blank" onClick={openDocument(product?.productTermsFileName || '')}>
                        <img className='maturity3__download-icon' src={downloadIcon} alt={'download icon'}/>
                        <p>Product terms</p>
                    </a>
                </li>
                <li>
                    <a href="#" target="_blank" className="fscs-link"
                       onClick={openDocument('https://www.investec.com/content/dam/united-kingdom/downloads-and-documents/private-banking/FSCS-Information-and-Exclusions.pdf')}>
                        <img className='maturity3__download-icon' src={downloadIcon} alt={'download icon'}/>
                        <p>FSCS information sheet</p>
                    </a>
                </li>
            </ul>
            <div className={'maturity3__section-break'}>
                <Checkbox
                    label="I have read the documents and agree to the terms."
                    onChange={() => setTermsConfirmed(!termsConfirmed)}
                    checked={termsConfirmed}
                />
            </div>
        </div>
    };

    return <>
        <h1>Confirm your instruction</h1>
        <div className="maturity3__info-box maturity3__info-box--column ">
            <div className={"maturity3__info-box__data-wrapper maturity3__info-box__data-wrapper--column"}>
                {props.instruction?.name === "RolloverAllFunds" ?
                    <p>You’ve chosen to: <b>Renew everything</b></p> :
                    <p>You’ve chosen to: <b>Renew original balance only</b></p>}
            </div>
            <div className={"maturity3__info-box__data-wrapper maturity3__info-box__data-wrapper--column"}>
                <p className={"maturity3__mobile-text"}>Fixed term: <b>{term}</b></p>
            </div>
            <div className={"maturity3__info-box__data-wrapper maturity3__info-box__data-wrapper--column"}>
                {displayNewToBankRate ?
                    <>
                        <p>As of today, you’ll earn: <b>{rate}% AER*</b></p>
                        <p className={"maturity3__multiline-text"}>We’ll always offer you our highest rate on the day
                            your account matures. Currently, this is our rate for new customers, which is higher than the
                            renewal rate we originally offered you.</p>
                    </>
                    :
                    <>
                        <p>Renewal rate: <b>{rate}% AER*</b></p>
                        <p className={"maturity3__multiline-text"}>We’ll always give you our highest rate on the day
                            your account matures, whether that’s this renewal rate or our new customer rate.</p>
                    </>
                }
            </div>
            <div className={"maturity3__info-box__data-wrapper maturity3__info-box__data-wrapper--column"}>
                {props.instruction?.name === "RolloverAllFunds" ?
                    <p>Total amount you’re renewing: <b data-di-mask>{formatCurrency(getCurrentBalanceWithCalculatedInterest(account))}</b></p> :
                    <p>Total amount you’re renewing: <b>{formatCurrency(account.balance)}</b></p>}
            </div>
            {props.instruction?.name === "RolloverPayawayInterest" &&
                <div className={"maturity3__info-box__data-wrapper maturity3__info-box__data-wrapper--column"}>
                    <p>Interest transferred to linked account: <b data-di-mask>{formatCurrency(getCalculatedInterest(account))}</b>
                    </p>
                </div>}
        </div>
        {data && renderSummaryBox()}
        <div className={"maturity3__flex-row maturity3__flex-row--left-align"}>
            <Button size='med' customClassNames={["navigation-button__back"]} label={"Back"} onClick={onClickBack}/>
            <ConfirmInstructionButton
                customClassName="navigation-button__continue"
                account={account}
                instruction={props.instruction}
                destinationAccount={props.selectedAccount}
                mobileNumber={props.mobileNumber}
                disabled={!termsConfirmed}
                multipleMaturingAccounts={props.multipleMaturingAccounts}
                onOTPSuccess={props.onStepComplete}
                showConfirmationOnSuccess={false}
                customAnalytics={() => rollOverAnalytics(account.product.code, product?.code, props.instruction?.name)}
            />
        </div>
        <div className={"maturity3__emphasis-box maturity3__emphasis-box--round maturity3__emphasis-box--blue maturity3__flex-row maturity3__flex-row--left-align"}>
            <img alt="info" className={"maturity3__inline-icon"} src={infoIcon}/>
            <p>You can change your instruction at any time
                before {account.maturity ? formatTime(constructDateTime(new Date(), account.maturity.maturityWindowCloseTime)) : ''} on {account.maturity ? formatDate(account.maturity.maturityWindowCloseDate) : ''}.</p>
        </div>
        <p className={"maturity3__aer-info"}>
            * AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest
            was paid and compounded
            once a year. The AER is intended to be an indicative rate to help you compare the return on different
            savings products.</p>
    </>
}

export default RollOverSummary